//
// Copyright © 2026 Link Studios
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
import type { Doc, Ref, Space } from '@hcengineering/core'

/*
 * Reglas estilo Notion para las issues del tracker: un disparador («cuando…»),
 * condiciones opcionales («y si…») y una o varias acciones («entonces…»).
 *
 * Las reglas, sus ejecuciones y las peticiones manuales son documentos de Huly
 * (dominio propio "lsautomation"), así que se ven en vivo, respetan permisos y
 * entran en el backup de Huly. Las referencias a issues, personas, estados, etc.
 * se guardan como string para no depender aquí de los plugins de Huly.
 */

/** Propiedades de la issue que entienden disparadores, condiciones y acciones. */
export type IssueAttribute =
  | 'title'
  | 'status'
  | 'assignee'
  | 'priority'
  | 'component'
  | 'milestone'
  | 'dueDate'
  | 'estimation'
  | 'labels'
  | 'kind'
  | 'createdBy'
  | 'parent'
  | 'project'

/** «Propiedad editada»: cómo debe haber cambiado. */
export type ChangeOp = 'any' | 'is' | 'isNot' | 'contains' | 'isSet' | 'cleared' | 'fromTo'

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6 // 0 = domingo

export type TriggerDef =
  | { kind: 'created' }
  | { kind: 'changed', attribute: IssueAttribute, op: ChangeOp, value?: any, from?: any, to?: any }
  | { kind: 'labelAdded', label?: string }
  | { kind: 'labelRemoved', label?: string }
  | { kind: 'comment', contains?: string }
  | { kind: 'subIssuesDone' }
  | {
    kind: 'schedule'
    every: 'day' | 'week' | 'month'
    time: string // HH:MM, hora de Bogotá
    weekday?: Weekday
    monthday?: number // 1-28
    start?: number
    end?: number
  }
  | { kind: 'manual', label?: string }

export type ConditionOp =
  | 'is'
  | 'isNot'
  | 'in'
  | 'notIn'
  | 'contains'
  | 'notContains'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'before'
  | 'after'
  | 'gt'
  | 'lt'

export interface ConditionDef {
  attribute: IssueAttribute
  op: ConditionOp
  /** Valor fijo; para fechas, también `{ days: n }` relativo a hoy. */
  value?: any
}

/** Un valor que puede depender de la issue o de quién disparó la regla. */
export type ValueDef =
  | { kind: 'literal', value: any }
  | { kind: 'empty' }
  | { kind: 'triggerPerson' }
  | { kind: 'creator' }
  | { kind: 'componentLead' }
  | { kind: 'previous' } // el valor anterior de la misma propiedad (p. ej. «quien la tenía antes»)
  | { kind: 'now', offsetDays?: number }
  | { kind: 'template', text: string }

export type RecipientDef =
  | { kind: 'person', person: string }
  | { kind: 'field', field: 'assignee' | 'creator' | 'triggerPerson' | 'componentLead' | 'collaborators' }
  | { kind: 'email', email: string }

export interface SetChange {
  attribute: IssueAttribute
  value: ValueDef
}

export type ActionDef =
  | { kind: 'set', attribute: IssueAttribute, value: ValueDef }
  | { kind: 'addLabel', label: string }
  | { kind: 'removeLabel', label: string }
  | { kind: 'comment', text: string, mentions?: RecipientDef[] }
  | { kind: 'notify', to: RecipientDef[], text: string }
  | {
    kind: 'createIssue'
    project?: string // vacío = el de la issue
    title: string
    asSubIssue?: boolean
    status?: string
    priority?: number
    assignee?: ValueDef
    labels?: string[]
    dueInDays?: number
  }
  | {
    kind: 'editIssues'
    target: 'subIssues' | 'parent' | 'siblings' | 'project'
    conditions?: ConditionDef[]
    changes: SetChange[]
  }
  | { kind: 'email', to: RecipientDef[], subject: string, text: string }
  | { kind: 'webhook', url: string, body?: string }
  | { kind: 'mattermost', url: string, text: string }

export interface VariableDef {
  name: string
  value: string // plantilla
}

export interface AutomationRule extends Doc {
  name: string
  description?: string
  enabled: boolean
  /** Proyectos donde aplica; vacío = todos. */
  projects: string[]
  triggerMode: 'any' | 'all'
  triggers: TriggerDef[]
  conditionMode: 'all' | 'any'
  conditions: ConditionDef[]
  variables?: VariableDef[]
  actions: ActionDef[]

  // Lo mantiene el motor
  runs?: number
  errors?: number
  consecutiveErrors?: number
  lastRunOn?: number
  lastScheduledOn?: number
  pausedReason?: string
}

export type RunStatus = 'ok' | 'error' | 'skipped' | 'dryRun'

/** Una ejecución de una regla: lo que hizo o por qué falló. */
export interface AutomationRun extends Doc {
  rule: Ref<AutomationRule>
  ruleName: string
  issue?: string
  issueIdentifier?: string
  trigger: string
  status: RunStatus
  changes: string[]
  error?: string
  /** Transacción que la disparó: evita repetir acciones si la cola reentrega un mensaje. */
  txId?: string
}

/** Petición desde la interfaz: ejecutar una regla manual o probar una regla sin aplicarla. */
export interface AutomationRequest extends Doc {
  rule: Ref<AutomationRule>
  issue: string
  dryRun: boolean
  status: 'pending' | 'done' | 'error'
  result?: string[]
  error?: string
}

/** Espacio donde viven reglas y peticiones: el del espacio de trabajo, visible para todos los miembros. */
export const automationSpace = 'core:space:Workspace' as Ref<Space>

/** Dominio (tabla) propio, para no mezclarse con los datos de ningún plugin de Huly. */
export const automationDomain = 'lsautomation'
